import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Trash2, Shield, Check, X, Key, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { API } from "@/context";

export function UserManagement() {
  const [users, setUsers] = useState([]);
  const [editingPassword, setEditingPassword] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", full_name: "" });
  const [isCreating, setIsCreating] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const deleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Delete user ${userEmail}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!newUser.email || !newUser.password || newUser.password.length < 6) {
      toast.error("Email and password (min 6 chars) required");
      return;
    }
    setIsCreating(true);
    try {
      await axios.post(`${API}/admin/create-user`, {
        email: newUser.email,
        password: newUser.password,
        full_name: newUser.full_name
      });
      toast.success("User created successfully");
      setNewUser({ email: "", password: "", full_name: "" });
      setIsCreateOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setIsCreating(false);
    }
  };

  const toggleUserAccess = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle-access`);
      toast.success("User access updated");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const promoteToAdmin = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/promote`);
      toast.success("User promoted to admin");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to promote user");
    }
  };

  const demoteFromAdmin = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/demote`);
      toast.success("User demoted to regular user");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to demote user");
    }
  };

  const resetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      const response = await axios.put(`${API}/admin/users/${userId}/reset-password?new_password=${encodeURIComponent(newPassword)}`);
      toast.success(`Password reset! New password: ${response.data.new_password}`);
      setEditingPassword(null);
      setNewPassword("");
    } catch (error) {
      toast.error("Failed to reset password");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-semibold tracking-tight">User Management</h2>
        <Button onClick={() => setIsCreateOpen(true)} className="bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] rounded-none" data-testid="create-user-btn">
          <Plus className="h-4 w-4 mr-2" /> Create User
        </Button>
      </div>
      <div className="border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold">Name</TableHead>
              <TableHead className="font-semibold">Email</TableHead>
              <TableHead className="font-semibold">Role</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">Joined</TableHead>
              <TableHead className="font-semibold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{user.full_name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <span className={`text-xs font-medium tracking-wide uppercase px-2 py-1 border ${user.role === "admin" ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]" : "bg-[#F4F4F5] border-border"}`}>
                    {user.role}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`text-xs font-medium px-2 py-1 rounded-sm ${user.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                    {user.is_active ? "Active" : "Disabled"}
                  </span>
                </TableCell>
                <TableCell className="text-sm">{new Date(user.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button onClick={() => toggleUserAccess(user.id)} size="sm" variant={user.is_active ? "destructive" : "default"} className="rounded-none h-8">
                      {user.is_active ? "Disable" : "Enable"}
                    </Button>
                    {user.role === "admin" ? (
                      <Button onClick={() => demoteFromAdmin(user.id)} size="sm" variant="outline" className="rounded-none h-8">Demote</Button>
                    ) : (
                      <Button onClick={() => promoteToAdmin(user.id)} size="sm" className="rounded-none h-8 bg-[var(--color-primary)] text-white">
                        <Shield className="h-3 w-3 mr-1" /> Make Admin
                      </Button>
                    )}
                    {editingPassword === user.id ? (
                      <div className="flex gap-2 items-center">
                        <Input type="text" placeholder="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="h-8 w-32 rounded-none border-2" minLength={6} />
                        <Button onClick={() => resetPassword(user.id)} size="sm" className="rounded-none h-8 bg-green-600 text-white"><Check className="h-3 w-3" /></Button>
                        <Button onClick={() => { setEditingPassword(null); setNewPassword(""); }} size="sm" variant="ghost" className="rounded-none h-8"><X className="h-3 w-3" /></Button>
                      </div>
                    ) : (
                      <Button onClick={() => setEditingPassword(user.id)} size="sm" variant="outline" className="rounded-none h-8">
                        <Key className="h-3 w-3 mr-1" /> Reset Password
                      </Button>
                    )}
                    {user.email !== "manager@analyticay.com" && (
                      <Button onClick={() => deleteUser(user.id, user.email)} size="sm" variant="destructive" className="rounded-none h-8" data-testid={`delete-user-${user.id}`}>
                        <Trash2 className="h-3 w-3 mr-1" /> Delete
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Create New User</DialogTitle>
            <DialogDescription>Only admins can create user accounts</DialogDescription>
          </DialogHeader>
          <form onSubmit={createUser} className="space-y-4 mt-4">
            <div>
              <label className="text-xs font-medium uppercase block mb-1">Full Name</label>
              <Input value={newUser.full_name} onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })} className="rounded-none border-2" placeholder="John Doe" data-testid="new-user-name" />
            </div>
            <div>
              <label className="text-xs font-medium uppercase block mb-1">Email</label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="rounded-none border-2" placeholder="user@analyticay.com" required data-testid="new-user-email" />
            </div>
            <div>
              <label className="text-xs font-medium uppercase block mb-1">Password</label>
              <Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="rounded-none border-2" placeholder="Min 6 characters" required minLength={6} data-testid="new-user-password" />
            </div>
            <Button type="submit" disabled={isCreating} className="w-full bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] rounded-none px-6 py-3" data-testid="submit-create-user">
              {isCreating ? "Creating..." : "Create User"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AuditLogs() {
  const [logs, setLogs] = useState([]);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/audit-logs`);
      setLogs(response.data);
    } catch (error) {
      console.error("Error fetching logs:", error);
      toast.error("Failed to load audit logs");
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const exportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/audit-logs/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'audit_logs.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to export audit logs");
    }
  };

  const clearLogs = async () => {
    if (!window.confirm("Are you sure you want to clear all audit logs? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/audit-logs`);
      toast.success("Audit logs cleared");
      fetchLogs();
    } catch (error) {
      toast.error("Failed to clear audit logs");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-semibold tracking-tight">Audit Logs - Status Change History</h2>
        <div className="flex gap-2">
          <Button onClick={exportCSV} className="rounded-none bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)]" disabled={logs.length === 0} data-testid="export-audit-logs-btn">
            <FileText className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Button onClick={clearLogs} variant="destructive" className="rounded-none" disabled={logs.length === 0} data-testid="clear-audit-logs-btn">
            <Trash2 className="h-4 w-4 mr-2" /> Clear Logs
          </Button>
        </div>
      </div>
      <div className="border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold">Timestamp</TableHead>
              <TableHead className="font-semibold">User</TableHead>
              <TableHead className="font-semibold">Event</TableHead>
              <TableHead className="font-semibold">Artwork</TableHead>
              <TableHead className="font-semibold">Old Status</TableHead>
              <TableHead className="font-semibold">New Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">No audit logs yet</TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="text-sm">{new Date(log.timestamp).toLocaleString()}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-sm">{log.user_name}</p>
                      <p className="text-xs text-muted-foreground">{log.user_email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{log.event_name}</TableCell>
                  <TableCell className="text-sm">{log.artwork_type}</TableCell>
                  <TableCell><span className="text-xs px-2 py-1 bg-[#F4F4F5] border border-border uppercase">{log.old_status}</span></TableCell>
                  <TableCell><span className="text-xs px-2 py-1 bg-[var(--color-accent)] text-white uppercase">{log.new_status}</span></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
